/** Cars and drivers (§5). Reads are member-level; writes are owner-only. */

import { z } from 'zod';

import { useType } from './enums.js';
import { count, optionalText, shortText, uuid } from './primitives.js';

const YEAR_MIN = 1900;
/** Roomy on purpose — people title cars by model year, which runs ahead. */
const YEAR_MAX = 2100;

export const createCarRequest = z.object({
  nickname: shortText(60),
  year: z.number().int().min(YEAR_MIN).max(YEAR_MAX).nullable().optional(),
  make: optionalText(60),
  model: optionalText(60),
  motorCode: optionalText(80),
  powerAdder: optionalText(60),
  transmission: optionalText(60),
  raceClass: optionalText(60),
  useType: useType.default('strip'),
  odometer: count.max(10_000_000).nullable().optional(),
});
export type CreateCarRequest = z.infer<typeof createCarRequest>;

export const updateCarRequest = createCarRequest.partial();
export type UpdateCarRequest = z.infer<typeof updateCarRequest>;

export const createDriverRequest = z.object({
  name: shortText(60),
  compNumber: optionalText(20),
});
export type CreateDriverRequest = z.infer<typeof createDriverRequest>;

export const updateDriverRequest = createDriverRequest.partial();
export type UpdateDriverRequest = z.infer<typeof updateDriverRequest>;

export const carParam = z.object({ garageId: uuid, carId: uuid });
export const driverParam = z.object({ garageId: uuid, driverId: uuid });

/**
 * `POST /cars/:id/image` — ask for a presigned upload.
 *
 * The client declares what it is about to send; the API turns that into a
 * presigned POST whose policy pins both. The byte cap is enforced at S3 by a
 * `content-length-range` condition, so a lying client still cannot exceed it
 * (§7) — this field only decides whether we bother minting the policy at all.
 */
export const IMAGE_MAX_BYTES = 400 * 1024;

export const IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/webp', 'image/png'] as const;

export const createImageUploadRequest = z.object({
  contentType: z.enum(IMAGE_CONTENT_TYPES),
  contentLength: z
    .number()
    .int()
    .min(1)
    .max(IMAGE_MAX_BYTES, `Resize below ${Math.floor(IMAGE_MAX_BYTES / 1024)} KB before uploading`),
});
export type CreateImageUploadRequest = z.infer<typeof createImageUploadRequest>;

export const createImageUploadResponse = z.object({
  /** Where to POST the multipart form. */
  url: z.string().url(),
  /** Policy fields that must be sent as form fields, in order, before the file. */
  fields: z.record(z.string()),
  /** The key the API will attach to the car once the upload lands. */
  key: z.string(),
  maxBytes: z.number().int(),
});
export type CreateImageUploadResponse = z.infer<typeof createImageUploadResponse>;
